import { BREAKPOINTS, CategoryPathUrls } from '~/bootcamp/types/bootcamp.types'

export const LEVELS = 1
export const PAGES_PER_LEVEL = 3

// Bootcamp overview module position on mobile view
export const MODULE_POSITION_MOBILE = [
  { top: 115, left: 60 },
  { top: 150, right: 70 },
  { top: 180, left: 60 },
  { top: 140, right: 80 },
  { top: 150, left: 80 },
  { top: 130, right: 70 },
  { top: 120, left: 70 },
  { top: 130, right: 70 },
  { top: 160, left: 80 },
  { top: 100, right: 70 },
]

// Bootcamp overview modules leave position on mobile view
export const LEAVES_POSITION_MOBILE = [
  { id: 'boot-leaf-1', style: { top: 232, left: 168, transform: 'scaleX(-1) scale(.5) rotate(275deg)' } },
  { id: 'boot-leaf-2', style: { top: 600, left: 118, transform: 'scaleX(-1) scale(0.5) rotate(247deg)' } },
  { id: 'boot-leaf-3', style: { top: 911, left: 142, transform: 'scaleX(-1) scale(0.5) rotate(247deg)' } },
]

export const MEDIA_QUERIES = {
  [BREAKPOINTS.MOBILE]: '(max-width: 767px)',
  [BREAKPOINTS.TABLET]: '(min-width: 768px) and (max-width: 1023px)',
  [BREAKPOINTS.DESKTOP]: '(min-width: 1024px)',
}

export const CATEGORY_PATHS = {
  numbersAndQuantities: 'numbers-and-quantities',
  statisticsAndProbability: 'statistics-and-probability',
  algebra: 'algebra',
  functions: 'functions',
  geometry: 'geometry',
  proofs: 'proofs',
  curriculum: 'curriculum',
} as const

export interface CategoryColorClasses {
  bgClass: string // Tailwind background class
  secondaryBgClass: string // Tailwind background class
  checkedBgClass: string // Tailwind pseudo checked background class
  gradientClass: string // Tailwind gradient class
  textClass: string // Tailwind text color class
  hex: string // Color Code
  borderClass: string // Tailwind border class
  outlineClass: string // Tailwind outline class
  ringClass: string // Tailwind ring class
  shadowBorderClass?: string // Tailwind border that matches the shadowClass
  shadowClass?: string // Tailwind shadow class
}

export const CATEGORY_COLORS: Record<CategoryPathUrls, CategoryColorClasses> = {
  algebra: {
    gradientClass: 'bg-task-algebra-gradient',
    bgClass: 'bg-blue-blueberry',
    secondaryBgClass: 'bg-blue-dark',
    checkedBgClass: 'checked:bg-blue-blueberry',
    textClass: 'text-blue-blueberry',
    hex: '#467BFF',
    borderClass: 'border-blue-blueberry',
    outlineClass: 'outline-algebra',
    ringClass: 'ring-blue-blueberry',
    shadowBorderClass: 'border-blue-dark',
    shadowClass: 'shadow-task-blue',
  },
  functions: {
    gradientClass: 'bg-task-functions-gradient',
    bgClass: 'bg-orange-portland',
    secondaryBgClass: 'bg-orange-darker',
    checkedBgClass: 'checked:bg-orange-portland',
    textClass: 'text-orange-portland',
    hex: '#FF5630',
    borderClass: 'border-orange-portland',
    outlineClass: 'outline-functions',
    ringClass: 'ring-orange-portland',
    shadowBorderClass: 'border-orange-darker',
    shadowClass: 'shadow-task-orange',
  },
  geometry: {
    gradientClass: 'bg-task-geometry-gradient',
    bgClass: 'bg-blue-medium-slate',
    secondaryBgClass: 'bg-blue-darker',
    checkedBgClass: 'checked:bg-blue-medium-slate',
    textClass: 'text-blue-medium-slate',
    hex: '#6966EC',
    borderClass: 'border-blue-medium-slate',
    outlineClass: 'outline-geometry',
    ringClass: 'ring-blue-medium-slate',
    shadowBorderClass: 'border-blue-darker',
    shadowClass: 'shadow-task-blue-dark',
  },
  'numbers-and-quantities': {
    gradientClass: 'bg-task-numbers-gradient',
    bgClass: 'bg-orchid-dark',
    secondaryBgClass: 'bg-orchid-dark',
    checkedBgClass: 'checked:bg-orchid-dark',
    textClass: 'text-orchid-dark',
    hex: '#A840C8',
    borderClass: 'border-orchid-dark',
    outlineClass: 'outline-numbers',
    ringClass: 'ring-orchid-dark',
    shadowBorderClass: 'border-violet-darker',
    shadowClass: 'shadow-task-purple',
  },
  curriculum: {
    gradientClass: 'bg-task-numbers-gradient',
    bgClass: 'bg-orchid-dark',
    secondaryBgClass: 'bg-orchid-dark',
    checkedBgClass: 'checked:bg-orchid-dark',
    textClass: 'text-orchid-dark',
    hex: '#A840C8',
    borderClass: 'border-orchid-dark',
    outlineClass: 'outline-numbers',
    ringClass: 'ring-orchid-dark',
    shadowBorderClass: 'border-violet-darker',
    shadowClass: 'shadow-task-purple',
  },
  proofs: {
    gradientClass: 'bg-task-proofs-gradient',
    bgClass: 'bg-orange-fulvous',
    secondaryBgClass: 'bg-orange-fulvous',
    checkedBgClass: 'checked:bg-orange-fulvous',
    textClass: 'text-orange-fulvous',
    hex: '#E17A00',
    borderClass: 'border-bootcamp-proof',
    outlineClass: 'outline-proof',
    ringClass: 'ring-bootcamp-proof',
    shadowBorderClass: 'border-orange-darkest',
    shadowClass: 'shadow-task-yellow',
  },
  'statistics-and-probability': {
    gradientClass: 'bg-task-statistics-gradient',
    bgClass: 'bg-pink-deep',
    secondaryBgClass: 'bg-pink-dark',
    checkedBgClass: 'checked:bg-pink-deep',
    textClass: 'text-pink-deep',
    hex: '#FA0E9A',
    borderClass: 'border-pink-deep',
    outlineClass: 'outline-statistics',
    ringClass: 'ring-pink-deep',
    shadowBorderClass: 'border-pink-dark',
    shadowClass: 'shadow-task-pink',
  },
}

export const XP_CONSTANTS = {
  complexityLevelMultiplier: 20,
  workloadLevelMultiplier: 25,
  difficultyLevelMultiplier: 15,
  minimumXP: 200,
}

export const GEM_CONSTANTS = {
  complexityLevelMultiplier: 1,
  workloadLevelMultiplier: 1,
  difficultyLevelMultiplier: 0.5,
  minimumGem: 1,
}

export const VIDEO_TOPIC_ICON: Record<CategoryPathUrls, { srcKey: string; height: number; width: number }> = {
  algebra: {
    srcKey: 'bootcamp/algebra-video-topic-icon.png',
    height: 13,
    width: 20,
  },
  functions: {
    srcKey: 'bootcamp/functions-video-topic-icon.png',
    height: 17,
    width: 10,
  },
  geometry: {
    srcKey: 'bootcamp/geometry-video-topic-icon.png',
    height: 18,
    width: 20,
  },
  'numbers-and-quantities': {
    srcKey: 'bootcamp/number-and-quantities-video-topic-icon.png',
    height: 18,
    width: 18,
  },
  curriculum: {
    srcKey: 'bootcamp/number-and-quantities-video-topic-icon.png',
    height: 18,
    width: 18,
  },
  proofs: {
    srcKey: 'bootcamp/proof-video-topic-icon.png',
    height: 13,
    width: 18,
  },
  'statistics-and-probability': {
    srcKey: 'bootcamp/statistics-and-probability-video-topic-icon.png',
    height: 18,
    width: 18,
  },
}

export const PROGRESS_BAR_CHEST: { openChestSrc: string; closedChestSrc: string } = {
  openChestSrc: 'onboarding/icons/updated-chest-open.png',
  closedChestSrc: 'onboarding/icons/updated-chest-purple.png',
}

export const GRADE_QUERY_PARAM = 'showGrade'
